import Link from "next/link";
import FloatingMenu from "components/floating_menu";
import ProblemReportDialog from "../problem_report_dialog";

import {
  FiLoader,
  FiPlay,
  FiMoreHorizontal,
  FiAlertTriangle
} from "react-icons/fi";
import { parentTopic, subTopic } from "utils/metadata";
import clsx from "clsx";
import { lessonsPath } from "utils/lessons";
import { track } from "utils/tracker";

import { useState, useEffect } from "react";
import { useNotifications } from "hooks/notifications";
import useLessonsCount from "hooks/questions/lessons_count";

import { Question as ApiQuestionProps } from "types/question";
import { QuestionnaireAnswer } from "types/questionnaire_answer";
import { QuestionnaireComponentProps } from "components/questionnaire";

interface QuestionMetadataProps extends ApiQuestionProps {
  completedQuestionnaire?: boolean;
  questionnaire?: QuestionnaireComponentProps;
  onFetchLessonsCount?: (count: number) => void;
  questionnaireAnswer?: QuestionnaireAnswer;
  loading?: boolean;
  disabled?: boolean;
}

export default function Metadata(props: QuestionMetadataProps): JSX.Element {
  const { id, institution, year, examNumber, topics, correctAlternative } =
    props;
  const { completedQuestionnaire, questionnaire, questionnaireAnswer } = props;
  const { loading } = props;
  const { onFetchLessonsCount } = props;
  const subject = parentTopic(topics);
  const topic = subTopic(topics);

  const [showMenu, setShowMenu] = useState(false);
  const [showProblemReportDialog, setShowProblemReportDialog] = useState(false);
  const { setNotification } = useNotifications();
  const { count: lessonsCount, isLoading: isLoadingLessonsCount } =
    useLessonsCount(subject?.slug, topic?.slug, !loading);

  useEffect(() => {
    onFetchLessonsCount && onFetchLessonsCount(lessonsCount);
  }, [lessonsCount, onFetchLessonsCount]);

  function handleLessonsClick(event) {
    track("Clicked lessons link on question", { origin: "Metadata" });
    if (lessonsCount) return;

    event.preventDefault();
    setNotification({
      type: "error",
      title: "Não encontramos aulas",
      text: "Ainda não temos aulas desse assunto <span style='white-space: nowrap;'>:(</span>"
    });
  }

  function enableReportDialog() {
    setShowMenu(false);
    setShowProblemReportDialog(true);
  }

  function borderColor() {
    const showRevealResult =
      completedQuestionnaire || questionnaireAnswer?.seenAnswer;
    if (!showRevealResult) return "border-primary-300";

    return correctAlternative === questionnaireAnswer?.answer
      ? "border-success"
      : "border-error";
  }

  return (
    <>
      <div
        className={clsx("fade-loading flex items-center justify-between", {
          loading
        })}
      >
        <div
          className={clsx(
            "metadata border-l-[3px] flex flex-col pl-3",
            borderColor()
          )}
        >
          <div className="exam-info flex items-center">
            <span className="institution mr-1 text-sm font-bold uppercase">
              {institution}
            </span>
            <span className="year mr-1 text-sm font-bold uppercase">
              {" "}
              {year}
            </span>
            <span className="question-number text-emphasis-medium text-sm">
              {" "}
              - Questão {examNumber}
            </span>
          </div>
          <div className="category-info">
            {parentTopic(topics) && !questionnaire?.seriousFrom && (
              <span className="subject text-sm font-semibold uppercase">
                {parentTopic(topics).name}
              </span>
            )}
            {subTopic(topics) && !questionnaire?.seriousFrom && (
              <span className="topic text-emphasis-medium text-sm font-semibold uppercase">
                {" "}
                - {subTopic(topics).name}
              </span>
            )}
          </div>
        </div>

        <div className="actions flex items-center">
          <Link href={lessonsCount ? lessonsPath({ subject, topic }) : "#"}>
            <a
              className="lessons-wrapper relative"
              onClick={handleLessonsClick}
            >
              <FiPlay size={22} />
              <div
                className={clsx(
                  "lessons-count-wrapper absolute -right-2 -top-3 flex items-center justify-center px-1 text-xs bg-primary-200 rounded-full",
                  { disabled: !lessonsCount }
                )}
              >
                <span className="min-w-[16px] flex items-center justify-center text-center text-xs">
                  {isLoadingLessonsCount ? (
                    <div className="py-[2px]">
                      <FiLoader className="animate-spin-slow" size={12} />
                    </div>
                  ) : (
                    lessonsCount
                  )}
                </span>
              </div>
            </a>
          </Link>

          <div className="relative ml-5">
            <div
              onClick={() => setShowMenu(true)}
              className="options-trigger cursor-pointer"
            >
              <FiMoreHorizontal size={22} />
            </div>
            {showMenu && (
              <FloatingMenu onChange={(visible) => setShowMenu(visible)}>
                <li onClick={enableReportDialog}>
                  <FiAlertTriangle size={18} />
                  Reportar problema
                </li>
              </FloatingMenu>
            )}
          </div>
        </div>
      </div>

      {showProblemReportDialog && (
        <ProblemReportDialog
          questionId={id}
          onClose={() => setShowProblemReportDialog(false)}
        />
      )}
    </>
  );
}
